import React, { FC, useMemo } from 'react';
import { Box, Chip, Grid, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { combineShoeSizeDisplay, getStandardValue } from '../../../helpers/equipment.helpers';
import { palette } from '../../../theme';

export interface LinkedEquipmentOwner {
  id?: number | null;
  firstname?: string | null;
  lastname?: string | null;
}

export interface LinkedEquipmentDetails {
  id: number;
  name: string;
  brand?: string | null;
  year?: number | null;
  public_id?: string | null;
  published?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  equipment_type?: { id?: number; name?: string | null } | null;
  equipment_subtype?: { id?: number; name?: string | null } | null;
  standard_field_values?: Record<string, unknown>;
  owner?: LinkedEquipmentOwner | null;
}

interface SpecField {
  apiLabel: string;
  shortLabel: string;
}

const SKI_CARD_SPEC_FIELDS: SpecField[] = [
  { apiLabel: 'Grind', shortLabel: 'GRIND' },
  { apiLabel: 'Camber Height', shortLabel: 'CAMBER HEIGHT' },
  { apiLabel: 'MF 0.2', shortLabel: 'MF 0.2' },
  { apiLabel: 'Snow Designation', shortLabel: 'SNOW DESIGNATION' },
  { apiLabel: 'Ski Length in cm', shortLabel: 'SKI LENGTH IN CM' },
  { apiLabel: 'Base Material', shortLabel: 'BASE MATERIAL' },
  { apiLabel: 'Serial #', shortLabel: 'SERIAL #' },
];

const SHOE_CARD_SPEC_FIELDS: SpecField[] = [
  { apiLabel: 'Stack Height', shortLabel: 'STACK HEIGHT' },
  { apiLabel: 'Drop', shortLabel: 'DROP' },
  { apiLabel: 'Cushioning', shortLabel: 'CUSHIONING' },
  { apiLabel: 'Terrain Type', shortLabel: 'TERRAIN TYPE' },
  { apiLabel: 'Weight', shortLabel: 'WEIGHT' },
  { apiLabel: 'Size', shortLabel: 'SIZE' },
];

function snowValueColor(display: string): string {
  const t = display.trim().toLowerCase();
  if (t.includes('cold')) return palette.primary.main;
  if (t.includes('warm')) return palette.error.main;
  if (t === 'zero') return palette.text.secondary;
  return palette.text.primary;
}

function getSubtypeChipSx(theme: Theme, name: string) {
  const base = {
    height: 22,
    fontSize: '0.7rem',
    fontWeight: 600,
    '& .MuiChip-label': { px: 1 },
  };
  const n = name.trim().toLowerCase();
  if (n === 'classic') {
    return { ...base, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' };
  }
  if (n === 'skate') {
    return { ...base, bgcolor: alpha(theme.palette.text.secondary, 0.12), color: 'text.primary' };
  }
  if (n === 'other') {
    return { ...base, bgcolor: 'grey.900', color: 'common.white' };
  }
  return { ...base, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' };
}

function getTypeChipSx(theme: Theme, typeName: string) {
  const base = {
    height: 22,
    fontSize: '0.7rem',
    fontWeight: 600,
    '& .MuiChip-label': { px: 1 },
  };
  if (typeName === 'xc skis') {
    return { ...base, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' };
  }
  if (typeName === 'shoes') {
    return { ...base, bgcolor: alpha(theme.palette.warning.main, 0.12), color: 'warning.main' };
  }
  if (typeName === 'other') {
    return { ...base, bgcolor: alpha(theme.palette.text.secondary, 0.12), color: 'text.primary' };
  }
  return { ...base, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' };
}

function getShoeSubtypeChipSx(theme: Theme, name: string) {
  const base = {
    height: 22,
    fontSize: '0.7rem',
    fontWeight: 600,
    '& .MuiChip-label': { px: 1 },
  };
  const n = name.trim().toLowerCase();
  if (n === 'running') {
    return { ...base, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' };
  }
  if (n === 'boots') {
    return { ...base, bgcolor: alpha(theme.palette.warning.main, 0.12), color: 'warning.main' };
  }
  if (n === 'hiking') {
    return { ...base, bgcolor: alpha(theme.palette.success.main, 0.12), color: 'success.main' };
  }
  if (n === 'cleats') {
    return { ...base, bgcolor: alpha(theme.palette.text.secondary, 0.12), color: 'text.primary' };
  }
  return { ...base, bgcolor: alpha(theme.palette.text.secondary, 0.12), color: 'text.primary' };
}

function formatOwnerAbbreviated(owner?: LinkedEquipmentOwner | null): string | null {
  if (!owner) return null;
  const first = owner.firstname?.trim();
  const last = owner.lastname?.trim();
  if (!first && !last) return null;
  if (!last) return first ?? null;
  const initial = last.charAt(0).toUpperCase();
  return first ? `${first} ${initial}.` : `${initial}.`;
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
}

function formatPublicId(publicId?: string | null): string | null {
  if (!publicId) return null;
  return `#${String(publicId).slice(0, 8).toUpperCase()}`;
}

export interface LinkedEquipmentCardProps {
  equipment: LinkedEquipmentDetails;
}

const LinkedEquipmentCard: FC<LinkedEquipmentCardProps> = ({ equipment }) => {
  const theme = useTheme();
  const sv = equipment.standard_field_values || {};
  const typeName = (equipment.equipment_type?.name ?? '').trim().toLowerCase();
  const isSkiType = typeName === 'xc skis';
  const isShoeType = typeName === 'shoes';
  const isOtherType = typeName === 'other';

  const brandYear = [equipment.brand?.trim(), equipment.year != null ? String(equipment.year) : '']
    .filter(Boolean)
    .join(' | ');

  const ownerLabel = formatOwnerAbbreviated(equipment.owner);
  const publicIdLabel = formatPublicId(equipment.public_id);

  const specFields = useMemo(() => {
    if (isSkiType) return SKI_CARD_SPEC_FIELDS;
    if (isShoeType) return SHOE_CARD_SPEC_FIELDS;
    return [];
  }, [isSkiType, isShoeType]);

  const specCells = useMemo(() => {
    return specFields.map(({ apiLabel, shortLabel }) => {
      let display = '—';
      if (shortLabel === 'SIZE' && isShoeType) {
        const sizeDisplay = combineShoeSizeDisplay(sv);
        display = sizeDisplay === 'N/A' ? '—' : sizeDisplay;
      } else {
        const entry = getStandardValue(sv, apiLabel);
        const val = entry?.value ?? '';
        const unit = entry?.unit ?? '';
        display = val ? (unit ? `${val} ${unit}` : val) : '—';
      }
      const valueColor = shortLabel === 'SNOW DESIGNATION' ? snowValueColor(display) : palette.text.primary;
      return { id: apiLabel, shortLabel, display, valueColor };
    });
  }, [isShoeType, specFields, sv]);

  const showSpecGrid = !isOtherType && specCells.length > 0;

  return (
    <Box
      sx={{
        border: `1px solid ${palette.divider}`,
        borderRadius: 2,
        bgcolor: palette.background.paper,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ color: palette.primary.main, fontWeight: 700, lineHeight: 1.3, wordBreak: 'break-word' }}
          >
            {equipment.name}
          </Typography>
          {brandYear && (
            <Typography variant="body2" sx={{ color: palette.text.secondary, mt: 0.25 }}>
              {brandYear}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
            {equipment.equipment_type?.name && (
              <Chip label={equipment.equipment_type.name} size="small" sx={getTypeChipSx(theme, typeName)} />
            )}
            {equipment.equipment_subtype?.name && (
              <Chip
                label={equipment.equipment_subtype.name}
                size="small"
                sx={
                  isShoeType
                    ? getShoeSubtypeChipSx(theme, equipment.equipment_subtype.name)
                    : getSubtypeChipSx(theme, equipment.equipment_subtype.name)
                }
              />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
          {publicIdLabel && (
            <Typography
              variant="caption"
              sx={{ color: palette.text.secondary, fontFamily: 'monospace', fontSize: '0.7rem' }}
            >
              {publicIdLabel}
            </Typography>
          )}
          {ownerLabel && (
            <Typography variant="body2" sx={{ color: palette.primary.main, fontWeight: 600 }}>
              {ownerLabel}
            </Typography>
          )}
        </Box>
      </Box>

      {showSpecGrid && (
        <Grid container spacing={2}>
          {specCells.map(({ id, shortLabel, display, valueColor }) => (
            <Grid item xs={6} key={id}>
              <Typography
                variant="caption"
                sx={{
                  color: palette.text.secondary,
                  fontWeight: 500,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  fontSize: '0.65rem',
                  display: 'block',
                }}
              >
                {shortLabel}
              </Typography>
              <Typography variant="body2" sx={{ color: valueColor, fontWeight: 700, mt: 0.25 }}>
                {display}
              </Typography>
            </Grid>
          ))}
        </Grid>
      )}

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          pt: showSpecGrid ? 0.5 : 0,
          borderTop: showSpecGrid ? `1px solid ${palette.divider}` : 'none',
        }}
      >
        <Typography variant="caption" sx={{ color: palette.text.secondary }}>
          Created:{' '}
          <Box component="span" sx={{ color: palette.text.primary, fontWeight: 600 }}>
            {formatDate(equipment.created_at)}
          </Box>
        </Typography>
        <Typography variant="caption" sx={{ color: palette.text.secondary }}>
          Updated:{' '}
          <Box component="span" sx={{ color: palette.text.primary, fontWeight: 600 }}>
            {formatDate(equipment.updated_at)}
          </Box>
        </Typography>
        <Typography variant="caption" sx={{ color: palette.text.secondary }}>
          Public:{' '}
          <Box component="span" sx={{ color: palette.text.primary, fontWeight: 600 }}>
            {equipment.published ? 'True' : 'False'}
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default LinkedEquipmentCard;
